import { useReactiveVar } from "@apollo/client";
import { AnchorHTMLAttributes, ReactNode, forwardRef } from "react";
import { Link as RouterLink } from "react-router-dom";

import { appVersionVar } from "@cache/cache";

import type { toObjectProps } from "./link-helpers";

let trackedVersion: null | string = null;

type Props = AnchorHTMLAttributes<HTMLAnchorElement> & {
  children?: ReactNode;
  to: string | toObjectProps;
  className?: string;
  id?: string;
  disabled?: boolean;
};

const AppLink = forwardRef<HTMLAnchorElement, Props>((props: Props, ref) => {
  const latestAppVersion = useReactiveVar(appVersionVar);

  if (props.disabled) {
    return (
      <span {...props} ref={ref}>
        {props.children}
      </span>
    );
  }
  // tracked version is empty on initial load
  if (!trackedVersion) {
    trackedVersion = latestAppVersion;
  }
  // We show an `a` tag when the app version has changed
  // so we can load a new build of the frontend app from server
  // https://github.com/ParmAngTech/manageros/issues/95
  else if (latestAppVersion && latestAppVersion !== trackedVersion) {
    const { children, to, ...rest } = props;
    const isStrTo = typeof to === "string";
    const href = isStrTo ? to : to.pathname;
    if (href && typeof href === "string") {
      return (
        <a {...rest} href={href} ref={ref}>
          {children}
        </a>
      );
    }
  }
  return <RouterLink {...props} ref={ref} />;
});
AppLink.displayName = "AppLink";

export default AppLink;
